// Compose sheet — moments only (voice notes coming later when R2 is enabled)
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { FeedPost, SpotifyTrack, PostColorHex, POST_COLORS } from '../types';

interface Props {
  onNavigate: (page: string) => void;
  replyToId?: string;
}

const DEFAULT_CLIP = 30;

// YouTube IFrame API type shim
declare global {
  interface Window {
    YT: {
      Player: new (el: HTMLElement, opts: object) => YTPlayer;
      PlayerState: { PLAYING: number; PAUSED: number; ENDED: number };
    };
    onYouTubeIframeAPIReady: () => void;
  }
}
interface YTPlayer {
  playVideo(): void;
  pauseVideo(): void;
  seekTo(seconds: number, allowSeekAhead: boolean): void;
  setVolume(vol: number): void;
  getPlayerState(): number;
  getCurrentTime(): number;
  destroy(): void;
}

import { loadYTApi } from '../utils/ytApi';

export function ComposePage({ onNavigate, replyToId }: Props) {
  const [postColor, setPostColor] = useState<PostColorHex | null>(null);
  const [caption, setCaption] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch parent post for reply context
  const [parentPost, setParentPost] = useState<FeedPost | null>(null);
  useEffect(() => {
    if (!replyToId) return;
    fetch(`/api/v1/posts/${replyToId}`)
      .then(r => r.ok ? r.json() : null)
      .then((j: unknown) => { const typed = j as { data: FeedPost } | null; if (typed) setParentPost(typed.data); })
      .catch(() => {});
  }, [replyToId]);

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SpotifyTrack[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedTrack, setSelectedTrack] = useState<SpotifyTrack | null>(null);

  // clip start in seconds
  const [timestamp, setTimestamp] = useState(0);
  // clip window length in seconds
  const [clipSecs, setClipSecs] = useState(DEFAULT_CLIP);

  // YouTube player state
  const [ytVideoId, setYtVideoId] = useState<string | null>(null);
  const [ytLoading, setYtLoading] = useState(false);
  const [ytReady, setYtReady] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [volume, setVolume] = useState(80);
  const [playheadPct, setPlayheadPct] = useState<number | null>(null);
  // Incremented to remount the YT container div after each destroyPlayer() call,
  // so createPlayer always receives a fresh DOM element (the IFrame API replaces the element).
  const [playerKey, setPlayerKey] = useState(0);
  const volumeRef = useRef(80);
  const ytPlayerRef = useRef<YTPlayer | null>(null);
  const ytContainerRef = useRef<HTMLDivElement | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Refs so the polling interval always sees current values
  const timestampRef = useRef(0);
  const clipSecsRef = useRef(DEFAULT_CLIP);

  // Scrubber drag
  const trackBarRef = useRef<HTMLDivElement | null>(null);
  const isDragging = useRef(false);
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clear pending search on unmount
  useEffect(() => () => { if (searchTimeout.current) clearTimeout(searchTimeout.current); }, []);

  // Keep refs in sync with state so interval closure sees current values
  useEffect(() => { timestampRef.current = timestamp; }, [timestamp]);
  useEffect(() => { clipSecsRef.current = clipSecs; }, [clipSecs]);

  // ─── YouTube player ───────────────────────────────────────────────────────

  function stopPoll() {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  }

  function startPoll() {
    stopPoll();
    pollRef.current = setInterval(() => {
      const player = ytPlayerRef.current;
      if (!player) { stopPoll(); return; }
      const cur = player.getCurrentTime();
      const start = timestampRef.current;
      const end = start + clipSecsRef.current;
      // Stop at clip end
      if (cur >= end) {
        player.pauseVideo();
        player.seekTo(start, true);
        setPlaying(false);
        setPlayheadPct(0);
        stopPoll();
        return;
      }
      // Update playhead position as % within the clip window
      const pct = ((cur - start) / clipSecsRef.current) * 100;
      setPlayheadPct(Math.max(0, Math.min(100, pct)));
    }, 80);
  }

  function destroyPlayer() {
    stopPoll();
    if (ytPlayerRef.current) {
      try { ytPlayerRef.current.destroy(); } catch { /* already gone */ }
      ytPlayerRef.current = null;
    }
    setYtReady(false);
    setPlaying(false);
    setPlayheadPct(null);
    // Bump key so React remounts the container div, giving createPlayer a clean element
    setPlayerKey(k => k + 1);
  }

  useEffect(() => () => { destroyPlayer(); }, []);

  function createPlayer(videoId: string) {
    if (!ytContainerRef.current) return;

    const player = new window.YT.Player(ytContainerRef.current, {
      videoId,
      playerVars: {
        autoplay: 0,
        controls: 0,
        disablekb: 1,
        fs: 0,
        modestbranding: 1,
        rel: 0,
        origin: window.location.origin,
      },
      events: {
        onReady: () => {
          player.setVolume(volumeRef.current);
          setYtReady(true);
          setYtLoading(false);
        },
        onStateChange: (e: { data: number }) => {
          if (e.data === window.YT.PlayerState.PLAYING) {
            setPlaying(true);
            startPoll();
          } else {
            setPlaying(false);
            stopPoll();
          }
        },
        onError: () => {
          setYtLoading(false);
          setYtReady(false);
          setError('Could not load video for this track — try a different one.');
        },
      },
    });
    ytPlayerRef.current = player;
  }

  // pendingVideoIdRef: set by fetchYouTubeVideo, consumed by the useEffect below
  // once the fresh container div is mounted (after playerKey bump).
  const pendingVideoIdRef = useRef<string | null>(null);

  const fetchYouTubeVideo = useCallback(async (track: SpotifyTrack) => {
    setYtVideoId(null);
    setYtReady(false);
    setYtLoading(true);
    setError(null);
    pendingVideoIdRef.current = null;
    try {
      const params = new URLSearchParams({ track: track.trackName });
      if (track.artist) params.set('artist', track.artist);
      if (track.trackId) params.set('spotify_track_id', track.trackId);
      const res = await fetch(`/api/v1/youtube/search?${params.toString()}`);
      const json = await res.json() as { videoId: string | null; debug?: unknown };
      if (!json.videoId) {
        setYtLoading(false);
        const reason = json.debug ? ` (${JSON.stringify(json.debug)})` : '';
        setError(`No video found for this track${reason} — timestamp will still be saved.`);
        return;
      }
      // Store the video ID; the useEffect below creates the player once the
      // container div is (re)mounted after the playerKey bump from destroyPlayer().
      pendingVideoIdRef.current = json.videoId;
      setYtVideoId(json.videoId);
    } catch {
      setYtLoading(false);
      setError('Could not load video preview.');
    }
  }, []); // eslint-disable-line

  // Create the YT player once we have both a fresh container and a pending video ID.
  useEffect(() => {
    const videoId = pendingVideoIdRef.current;
    if (!videoId || !ytContainerRef.current) return;
    pendingVideoIdRef.current = null;
    loadYTApi(() => {
      if (ytContainerRef.current) createPlayer(videoId);
    });
  }, [playerKey, ytVideoId]); // eslint-disable-line

  function togglePlay() {
    if (!ytPlayerRef.current || !ytReady) return;
    const state = ytPlayerRef.current.getPlayerState();
    if (state === window.YT.PlayerState.PLAYING) {
      ytPlayerRef.current.pauseVideo();
    } else {
      ytPlayerRef.current.seekTo(timestamp, true);
      ytPlayerRef.current.playVideo();
    }
  }

  function handleVolumeChange(v: number) {
    volumeRef.current = v;
    setVolume(v);
    if (ytPlayerRef.current) ytPlayerRef.current.setVolume(v);
  }

  // ─── Scrubber ─────────────────────────────────────────────────────────────

  function clamp(val: number, min: number, max: number) {
    return Math.max(min, Math.min(max, val));
  }

  function secFromPointer(clientX: number, durationSec: number): number {
    const bar = trackBarRef.current;
    if (!bar) return 0;
    const rect = bar.getBoundingClientRect();
    const ratio = clamp((clientX - rect.left) / rect.width, 0, 1);
    return clamp(ratio * durationSec, 0, durationSec - clipSecs);
  }

  function onBarPointerDown(e: React.PointerEvent<HTMLDivElement>) {
    if (!selectedTrack) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    isDragging.current = true;
    const dur = selectedTrack.durationMs / 1000;
    const newTs = secFromPointer(e.clientX, dur);
    timestampRef.current = newTs;
    setTimestamp(newTs);
    setPlayheadPct(0);
    if (ytPlayerRef.current && ytReady) {
      ytPlayerRef.current.seekTo(newTs, true);
      ytPlayerRef.current.playVideo();
    }
  }

  function onBarPointerMove(e: React.PointerEvent<HTMLDivElement>) {
    if (!isDragging.current || !selectedTrack) return;
    const dur = selectedTrack.durationMs / 1000;
    const newTs = secFromPointer(e.clientX, dur);
    timestampRef.current = newTs;
    setTimestamp(newTs);
    setPlayheadPct(0);
    if (ytPlayerRef.current && ytReady) {
      ytPlayerRef.current.seekTo(newTs, true);
    }
  }

  function onBarPointerUp() {
    isDragging.current = false;
  }

  function handleClipChange(secs: number) {
    clipSecsRef.current = secs;
    setClipSecs(secs);
    setPlayheadPct(0);
    if (selectedTrack) {
      const durationSec = selectedTrack.durationMs / 1000;
      setTimestamp(ts => {
        const clamped = clamp(ts, 0, durationSec - secs);
        timestampRef.current = clamped;
        return clamped;
      });
    }
  }

  // ─── Search ───────────────────────────────────────────────────────────────

  function onSearchChange(q: string) {
    setSearchQuery(q);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    if (!q.trim()) { setSearchResults([]); return; }
    searchTimeout.current = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const res = await fetch(`/api/v1/spotify/search?q=${encodeURIComponent(q)}`);
        const json = await res.json() as { data?: SpotifyTrack[]; error?: string };
        if (json.error) { setError(`Search failed: ${json.error}`); setSearchResults([]); }
        else {
          setSearchResults(json.data ?? []);
          if ((json.data ?? []).length === 0) setError('No tracks found');
          else setError(null);
        }
      } catch { setError('Search request failed'); }
      finally { setSearchLoading(false); }
    }, 500);
  }

  function selectTrack(track: SpotifyTrack) {
    destroyPlayer();
    setSelectedTrack(track);
    setTimestamp(0);
    setClipSecs(Math.min(DEFAULT_CLIP, Math.floor(track.durationMs / 1000)));
    setSearchResults([]);
    setSearchQuery(track.trackName);
    setError(null);
    fetchYouTubeVideo(track);
  }

  function clearTrack() {
    destroyPlayer();
    setYtVideoId(null);
    setSelectedTrack(null);
    setSearchQuery('');
    setTimestamp(0);
    setClipSecs(DEFAULT_CLIP);
    setError(null);
  }

  // ─── Submit ───────────────────────────────────────────────────────────────

  async function handleSubmit() {
    const isTextReply = !!replyToId && !selectedTrack;
    if (!selectedTrack && !isTextReply) { setError('Select a track'); return; }
    if (isTextReply && !caption.trim()) { setError('Add a caption to reply'); return; }
    destroyPlayer();
    setSubmitting(true);
    setError(null);
    try {
      const body: Record<string, unknown> = {
        content_type: 'moment',
        post_color: postColor ?? undefined,
        caption: caption.trim() || undefined,
        parent_post_id: replyToId ?? undefined,
      };
      if (selectedTrack) {
        body.spotify_track_id = selectedTrack.trackId;
        body.spotify_track_name = selectedTrack.trackName;
        body.spotify_artist = selectedTrack.artist;
        body.spotify_preview_url = selectedTrack.previewUrl ?? undefined;
        body.timestamp_start_ms = Math.round(timestamp * 1000);
        body.clip_duration_ms = Math.round(clipSecs * 1000);
        body.duration_ms = selectedTrack.durationMs;
        body.youtube_video_id = ytVideoId ?? undefined;
      }
      const res = await fetch('/api/v1/posts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json() as { error: string };
        throw new Error(err.error ?? 'Post failed');
      }
      onNavigate('feed');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong');
    } finally {
      setSubmitting(false);
    }
  }

  // ─── Derived ──────────────────────────────────────────────────────────────

  const durationSec = selectedTrack ? selectedTrack.durationMs / 1000 : 0;
  const clipEnd = Math.min(timestamp + clipSecs, durationSec);
  const windowLeft = durationSec > 0 ? (timestamp / durationSec) * 100 : 0;
  const windowWidth = durationSec > 0 ? (clipSecs / durationSec) * 100 : 0;

  // Tick marks targeting ~8 ticks at clean intervals
  const tickInterval = durationSec > 0
    ? (() => {
        const raw = durationSec / 8;
        const steps = [5, 10, 15, 20, 30, 60, 90, 120, 180, 240, 300];
        return steps.find(s => s >= raw) ?? 300;
      })()
    : 30;
  const ticks: number[] = [];
  if (durationSec > 0) {
    for (let t = 0; t < durationSec - tickInterval * 0.4; t += tickInterval) ticks.push(t);
    ticks.push(durationSec);
  }

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div style={pageStyle}>
      <header style={headerStyle}>
        <button onClick={() => onNavigate('feed')} style={closeStyle} aria-label="Close">
          <CloseIcon />
        </button>
        <h2 style={{ fontSize: 17, fontWeight: 600 }}>{replyToId ? 'Reply' : 'New Moment'}</h2>
        <button onClick={handleSubmit} disabled={submitting} style={postBtnStyle(submitting)}>
          {submitting ? '...' : 'Post'}
        </button>
      </header>

      <div style={bodyStyle}>
        {/* Reply-to context banner */}
        {replyToId && (
          <div style={replyContextStyle}>
            <ReplyIcon />
            {parentPost ? (
              <div style={{ minWidth: 0 }}>
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Replying to </span>
                <span style={{ fontSize: 12, fontWeight: 600 }}>
                  {parentPost.author_display_name ?? parentPost.author_username ?? 'someone'}
                </span>
                {parentPost.spotify_track_name && (
                  <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                    {' · '}{parentPost.spotify_track_name}
                  </span>
                )}
                {!parentPost.spotify_track_name && parentPost.caption && (
                  <span style={{ fontSize: 12, color: 'var(--text-muted)', display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    "{parentPost.caption.slice(0, 60)}{parentPost.caption.length > 60 ? '…' : ''}"
                  </span>
                )}
              </div>
            ) : (
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Replying to a moment…</span>
            )}
          </div>
        )}

        {error && <div style={errorStyle}>{error}</div>}

        {/* Track search */}
        <div style={sectionStyle}>
          <label style={labelStyle}>
            Track {replyToId && <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>optional</span>}
          </label>
          <input
            value={searchQuery}
            onChange={e => { setError(null); onSearchChange(e.target.value); }}
            placeholder="Search for a song..."
            style={inputStyle}
            autoComplete="off"
          />
          {searchLoading && (
            <div style={{ fontSize: 12, color: 'var(--text-muted)', padding: '4px 0' }}>Searching...</div>
          )}
          {searchResults.length > 0 && !selectedTrack && (
            <div style={resultsStyle}>
              {searchResults.map(track => (
                <button
                  key={track.trackId}
                  style={trackResultStyle}
                  onMouseDown={e => { e.preventDefault(); selectTrack(track); }}
                >
                  {track.albumArt && (
                    <img src={track.albumArt} alt="" style={{ width: 36, height: 36, borderRadius: 4, objectFit: 'cover' }} />
                  )}
                  <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                    <div style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {track.trackName}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{track.artist}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
          {selectedTrack && (
            <div style={selectedTrackStyle}>
              {selectedTrack.albumArt && (
                <img src={selectedTrack.albumArt} alt="" style={{ width: 44, height: 44, borderRadius: 6, objectFit: 'cover' }} />
              )}
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{selectedTrack.trackName}</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{selectedTrack.artist}</div>
              </div>
              <button onClick={clearTrack} style={{ color: 'var(--text-muted)', padding: 4 }}>
                <CloseIcon />
              </button>
            </div>
          )}
        </div>

        {/* Clip picker */}
        {selectedTrack && (
          <div style={sectionStyle}>

            {/* Hidden YouTube player container — keyed so React remounts a fresh div
                each time destroyPlayer() is called, giving the IFrame API a clean element */}
            <div style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', opacity: 0, pointerEvents: 'none' }}>
              <div key={playerKey} ref={ytContainerRef} />
            </div>

            {/* Clip length chooser */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <label style={labelStyle}>Clip</label>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                {[5, 10, 15, 30, 45, 60].filter(s => s <= durationSec).map(s => (
                  <button
                    key={s}
                    onClick={() => handleClipChange(s)}
                    style={clipPillStyle(clipSecs === s)}
                  >
                    {s}s
                  </button>
                ))}
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <input
                    type="number"
                    min={1}
                    max={Math.floor(durationSec)}
                    value={clipSecs}
                    onChange={e => {
                      const v = Math.max(1, Math.min(Math.floor(durationSec), parseInt(e.target.value) || 1));
                      handleClipChange(v);
                    }}
                    style={{
                      width: 52, padding: '4px 6px', fontSize: 12, textAlign: 'center',
                      background: 'var(--surface2)', border: '1px solid var(--border)',
                      borderRadius: 'var(--radius-sm)', color: 'var(--text)',
                    }}
                  />
                  <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>s</span>
                </div>
              </div>
            </div>

            {/* Timeline bar */}
            <div style={{ position: 'relative', paddingBottom: 18 }}>

              <div
                ref={trackBarRef}
                style={trackBarStyle}
                onPointerDown={onBarPointerDown}
                onPointerMove={onBarPointerMove}
                onPointerUp={onBarPointerUp}
                onPointerCancel={onBarPointerUp}
              >
                {/* Track background */}
                <div style={trackBgStyle} />

                {/* Dimmed regions outside selection */}
                {windowLeft > 0 && (
                  <div style={{ ...dimStyle, left: 0, width: `${windowLeft}%` }} />
                )}
                {windowLeft + windowWidth < 100 && (
                  <div style={{ ...dimStyle, left: `${windowLeft + windowWidth}%`, right: 0 }} />
                )}

                {/* Selection window */}
                <div style={{ ...selectionWindowStyle, left: `${windowLeft}%`, width: `${windowWidth}%` }}>
                  {/* Playhead — moves within clip window while playing */}
                  {playheadPct !== null && (
                    <div style={{
                      position: 'absolute',
                      top: 0, bottom: 0,
                      left: `${playheadPct}%`,
                      width: 2,
                      background: '#fff',
                      borderRadius: 1,
                      opacity: 0.9,
                      pointerEvents: 'none',
                    }} />
                  )}
                </div>

                {/* Tick lines */}
                {ticks.map(t => (
                  <div key={t} style={{
                    position: 'absolute',
                    left: `${(t / durationSec) * 100}%`,
                    top: 0, bottom: 0, width: 1,
                    background: 'rgba(255,255,255,0.12)',
                    pointerEvents: 'none',
                  }} />
                ))}

                {/* Edge handles */}
                <div style={{ ...handleStyle, left: `${windowLeft}%` }} />
                <div style={{ ...handleStyle, left: `${windowLeft + windowWidth}%` }} />
              </div>

              {/* Tick labels */}
              <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0 }}>
                {ticks.map(t => {
                  const pct = (t / durationSec) * 100;
                  const isFirst = t === 0;
                  const isLast = t >= durationSec - 1;
                  return (
                    <span key={t} style={{
                      position: 'absolute',
                      left: `${pct}%`,
                      transform: isFirst ? 'none' : isLast ? 'translateX(-100%)' : 'translateX(-50%)',
                      fontSize: 9, color: 'var(--text-muted)',
                      whiteSpace: 'nowrap', pointerEvents: 'none',
                    }}>
                      {formatSec(t)}
                    </span>
                  );
                })}
              </div>
            </div>

            {/* Range label */}
            <div style={rangeLabelStyle}>
              <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatSec(timestamp)}</span>
              <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>{clipSecs}s clip</span>
              <span style={{ fontVariantNumeric: 'tabular-nums' }}>{formatSec(clipEnd)}</span>
            </div>

            {/* Play/pause + volume */}
            <div style={previewRowStyle}>
              <button
                onClick={togglePlay}
                disabled={ytLoading || !ytReady}
                style={previewPlayBtnStyle(playing)}
                title={ytLoading ? 'Loading…' : !ytVideoId ? 'No video found' : playing ? 'Pause' : 'Play from this position'}
              >
                {ytLoading ? <LoadingDots /> : playing ? <PauseIcon /> : <PlayIcon />}
              </button>
              <VolumeIcon muted={volume === 0} />
              <input
                type="range" min={0} max={100} step={1} value={volume}
                onChange={e => handleVolumeChange(Number(e.target.value))}
                style={{ ...sliderStyle, flex: 1 }}
              />
              <span style={{ fontSize: 11, color: 'var(--text-muted)', minWidth: 28, textAlign: 'right' }}>
                {volume}%
              </span>
            </div>

            {!ytLoading && !ytVideoId && !error && (
              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                No video preview available — timestamp will still be saved.
              </div>
            )}
          </div>
        )}

        {/* Color */}
        <div style={sectionStyle}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <label style={labelStyle}>
              Color <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 400 }}>optional</span>
            </label>
            {postColor && (
              <button onClick={() => setPostColor(null)} style={{ fontSize: 11, color: 'var(--text-muted)' }}>Clear</button>
            )}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {POST_COLORS.map(c => (
              <button
                key={c.hex}
                onClick={() => setPostColor(postColor === c.hex ? null : c.hex)}
                title={`${c.label} — ${c.vibe}`}
                style={{
                  width: 28, height: 28, borderRadius: '50%',
                  background: c.hex,
                  border: postColor === c.hex ? '3px solid var(--text)' : '2px solid transparent',
                  outline: postColor === c.hex ? `2px solid ${c.hex}` : 'none',
                  outlineOffset: 2,
                  cursor: 'pointer',
                  transition: 'transform 0.1s',
                  transform: postColor === c.hex ? 'scale(1.2)' : 'scale(1)',
                  flexShrink: 0,
                }}
              />
            ))}
          </div>
          {postColor && (
            <div style={{ marginTop: 8, fontSize: 11, color: 'var(--text-muted)' }}>
              {POST_COLORS.find(c => c.hex === postColor)?.label} — {POST_COLORS.find(c => c.hex === postColor)?.vibe}
            </div>
          )}
        </div>

        {/* Caption */}
        <div style={sectionStyle}>
          <label style={labelStyle}>Caption (optional)</label>
          <textarea
            value={caption}
            onChange={e => setCaption(e.target.value)}
            placeholder="What's on your mind?"
            rows={3}
            style={{ ...inputStyle, resize: 'none' }}
            maxLength={280}
          />
        </div>
      </div>
    </div>
  );
}

// ─── Icons ────────────────────────────────────────────────────────────────────

function CloseIcon() {
  return (
    <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function ReplyIcon() {
  return (
    <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
      <path d="M9 14l-4-4 4-4" /><path d="M5 10h11a4 4 0 010 8h-1" />
    </svg>
  );
}

function PlayIcon() {
  return (
    <svg width={16} height={16} viewBox="0 0 24 24" fill="currentColor">
      <polygon points="5,3 19,12 5,21" />
    </svg>
  );
}

function PauseIcon() {
  return (
    <svg width={16} height={16} viewBox="0 0 24 24" fill="currentColor">
      <rect x="6" y="4" width="4" height="16" rx="1" />
      <rect x="14" y="4" width="4" height="16" rx="1" />
    </svg>
  );
}

function VolumeIcon({ muted }: { muted: boolean }) {
  return muted ? (
    <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
      <polygon points="11,5 6,9 2,9 2,15 6,15 11,19" />
      <line x1="23" y1="9" x2="17" y2="15" /><line x1="17" y1="9" x2="23" y2="15" />
    </svg>
  ) : (
    <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
      <polygon points="11,5 6,9 2,9 2,15 6,15 11,19" />
      <path d="M15.54 8.46a5 5 0 010 7.07" />
      <path d="M19.07 4.93a10 10 0 010 14.14" />
    </svg>
  );
}

function LoadingDots() {
  return (
    <div style={{ display: 'flex', gap: 3, alignItems: 'center' }}>
      {[0, 1, 2].map(i => (
        <div key={i} style={{ width: 4, height: 4, borderRadius: '50%', background: 'currentColor', animation: `pulse 1.2s ease-in-out ${i * 0.2}s infinite` }} />
      ))}
    </div>
  );
}

// ─── Util ─────────────────────────────────────────────────────────────────────

function formatSec(s: number): string {
  const n = Math.floor(s);
  return `${Math.floor(n / 60)}:${String(n % 60).padStart(2, '0')}`;
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const pageStyle: React.CSSProperties = {
  flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', paddingBottom: 24,
};

const headerStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  padding: '12px 16px', borderBottom: '1px solid var(--border)',
  position: 'sticky', top: 0, background: 'var(--bg)', zIndex: 10,
};

const closeStyle: React.CSSProperties = { color: 'var(--text-muted)', padding: 4 };

const postBtnStyle = (disabled: boolean): React.CSSProperties => ({
  padding: '6px 18px', borderRadius: 100,
  background: disabled ? 'var(--surface2)' : 'var(--accent)',
  color: disabled ? 'var(--text-muted)' : '#fff',
  fontSize: 14, fontWeight: 600, transition: 'all 0.15s',
});

const bodyStyle: React.CSSProperties = {
  flex: 1, padding: '0 16px', display: 'flex', flexDirection: 'column',
};

const sectionStyle: React.CSSProperties = {
  paddingTop: 20, display: 'flex', flexDirection: 'column', gap: 8,
};

const labelStyle: React.CSSProperties = {
  fontSize: 12, fontWeight: 600, color: 'var(--text-muted)',
  textTransform: 'uppercase', letterSpacing: '0.05em',
};

const inputStyle: React.CSSProperties = {
  background: 'var(--surface)', border: '1px solid var(--border)',
  borderRadius: 'var(--radius-sm)', padding: '10px 12px',
  fontSize: 14, width: '100%', color: 'var(--text)',
};

const resultsStyle: React.CSSProperties = {
  background: 'var(--surface)', border: '1px solid var(--border)',
  borderRadius: 'var(--radius-sm)', overflow: 'hidden',
  maxHeight: 240, overflowY: 'auto',
};

const trackResultStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 10,
  width: '100%', padding: '10px 12px',
  borderBottom: '1px solid var(--border)', background: 'none',
};

const selectedTrackStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 12,
  padding: '10px 12px', background: 'var(--surface)',
  borderRadius: 'var(--radius-sm)', border: '1px solid var(--accent)',
};

const clipPillStyle = (active: boolean): React.CSSProperties => ({
  padding: '3px 10px', borderRadius: 100, fontSize: 12, fontWeight: 600,
  background: active ? 'var(--accent)' : 'var(--surface)',
  color: active ? '#fff' : 'var(--text-muted)',
  border: `1px solid ${active ? 'var(--accent)' : 'var(--border)'}`,
  transition: 'all 0.12s', cursor: 'pointer',
});

const trackBarStyle: React.CSSProperties = {
  position: 'relative', height: 44, borderRadius: 8,
  cursor: 'pointer', userSelect: 'none', touchAction: 'none',
};

const trackBgStyle: React.CSSProperties = {
  position: 'absolute', inset: '10px 0', borderRadius: 4,
  background: 'var(--surface2)',
};

const dimStyle: React.CSSProperties = {
  position: 'absolute', top: 10, bottom: 10,
  background: 'rgba(0,0,0,0.5)', pointerEvents: 'none',
};

const selectionWindowStyle: React.CSSProperties = {
  position: 'absolute', top: 8, bottom: 8,
  borderRadius: 4, background: 'var(--accent)',
  opacity: 0.85, pointerEvents: 'none',
};

const handleStyle: React.CSSProperties = {
  position: 'absolute', top: '50%', marginTop: -14,
  width: 3, height: 28, borderRadius: 2, background: '#fff',
  boxShadow: '0 1px 6px rgba(0,0,0,0.5)', transform: 'translateX(-50%)',
  pointerEvents: 'none',
};

const rangeLabelStyle: React.CSSProperties = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  fontSize: 12, fontWeight: 600, color: 'var(--text)',
};

const sliderStyle: React.CSSProperties = { width: '100%', accentColor: 'var(--accent)' };

const previewRowStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 10,
};

const previewPlayBtnStyle = (active: boolean): React.CSSProperties => ({
  width: 34, height: 34, borderRadius: 8, flexShrink: 0,
  background: active ? 'var(--accent)' : 'var(--surface)',
  border: `1px solid ${active ? 'var(--accent)' : 'var(--border)'}`,
  color: active ? '#fff' : 'var(--accent)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  transition: 'all 0.15s', cursor: 'pointer',
});


const replyContextStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'flex-start', gap: 8,
  marginTop: 12, padding: '10px 12px',
  background: 'var(--surface)', border: '1px solid var(--border)',
  borderRadius: 'var(--radius-sm)',
  color: 'var(--accent)',
};

const errorStyle: React.CSSProperties = {
  marginTop: 12, padding: '10px 12px',
  background: '#ef444420', border: '1px solid #ef444440',
  borderRadius: 'var(--radius-sm)', fontSize: 13, color: '#ef4444',
};
